import { AllClientAccountsService } from '#app/domain/clients/AllClientAccountsService';

import {
    clientMatchesActivesAllowedIds,
    collectCrmClientActivesMatchIds,
    crmClientActivesUserId,
} from '../clients/crmClientActivesUserId';
import { fetchCrmActiveClientIdsForManager } from '../clients/fetchCrmActiveClientIdsForManager';
import { fetchCrmArchivedClientIdsForManager } from '../clients/fetchCrmArchivedClientIdsForManager';

/** Как в CRM: архивный основной контакт не участвует в «Активы». */
const isArchivedMainContact = (client: any): boolean => {
    const c = client as {
        mainContact?: { is_archive?: boolean };
        main_contact?: { is_archive?: boolean };
    };
    return Boolean(c?.mainContact?.is_archive ?? c?.main_contact?.is_archive);
};

/** Если CRM не ответил — когорта из WhiteSwan (уже `manager_id`). */
const buildAllowedIdsFromWsClients = (clients: any): Set<number> => {
    const raw = Array.isArray(clients) ? clients : [];
    const ids = new Set<number>();
    for (const c of raw) {
        const pk = crmClientActivesUserId(c as Parameters<typeof crmClientActivesUserId>[0]);
        if (pk != null) {
            ids.add(pk);
        }
        const row = c as { user_id?: any };
        const uid = Number(row?.user_id);
        if (Number.isFinite(uid) && uid > 0) {
            ids.add(uid);
        }
    }
    return ids;
};

const partitionClientsDropArchived = (
    clients: any,
    crmArchivedIds: Set<number>,
    crmAllowedIds: Set<number>,
    useActiveWhitelist: boolean
): { activeClients: any[]; archivedUserIds: Set<number> } => {
    const raw = Array.isArray(clients) ? clients : [];
    const archivedUserIds = new Set<number>(crmArchivedIds);
    raw.forEach((c: any) => {
        if (!isArchivedMainContact(c)) {
            return;
        }
        for (const id of collectCrmClientActivesMatchIds(c as Parameters<typeof collectCrmClientActivesMatchIds>[0])) {
            archivedUserIds.add(id);
        }
    });
    const activeClients = raw.filter((c: any) => {
        if (useActiveWhitelist) {
            if (crmAllowedIds.size === 0) {
                return false;
            }
            return clientMatchesActivesAllowedIds(
                c as Parameters<typeof clientMatchesActivesAllowedIds>[0],
                crmAllowedIds
            );
        }
        const matchIds = collectCrmClientActivesMatchIds(
            c as Parameters<typeof collectCrmClientActivesMatchIds>[0]
        );
        if (matchIds.some((id) => archivedUserIds.has(id))) {
            return false;
        }
        return true;
    });
    return { activeClients, archivedUserIds };
};

/** Ждём полный ответ WhiteSwan (колбэк `onAccountsEnriched`), как в UI до кеша. */
function fetchClientsHeavy(managerId: string | number): Promise<any> {
    return new Promise((resolve, reject) => {
        const to = window.setTimeout(() => reject(new Error('actives_clients_heavy_timeout')), 120_000);
        void AllClientAccountsService.do(managerId, (heavy) => {
            window.clearTimeout(to);
            resolve(heavy);
        })
            .catch((e) => {
                window.clearTimeout(to);
                reject(e);
            });
    });
}

export type FunnelActivesClientsPack = {
    clients: any[];
    useCrmWhitelist: boolean;
    cohortAllowedUserIds: number[];
    archivedUserIdsForFilter: number[];
};

/**
 * Один «пакет» для вкладки Активы: CRM-когорта + полные клиенты с счетами.
 * Кешируется RTK Query по managerId.
 */
export async function loadFunnelActivesClientsPack(managerId: string | number): Promise<FunnelActivesClientsPack> {
    let crmFetchOk = true;
    const cohortP = Promise.all([
        fetchCrmActiveClientIdsForManager(managerId).catch(() => {
            crmFetchOk = false;
            return new Set<number>();
        }),
        fetchCrmArchivedClientIdsForManager(managerId).catch(() => new Set<number>()),
    ]).then(([activeIds, archivedIds]) => ({ activeIds, archivedIds }));

    const [fullHeavy, { activeIds, archivedIds }] = await Promise.all([
        fetchClientsHeavy(managerId),
        cohortP,
    ]);

    let allowedIds = activeIds;
    if (allowedIds.size === 0) {
        allowedIds = buildAllowedIdsFromWsClients(fullHeavy);
    }

    const useActiveWhitelist = crmFetchOk || allowedIds.size > 0;
    const { activeClients, archivedUserIds } = partitionClientsDropArchived(
        fullHeavy,
        archivedIds,
        allowedIds,
        useActiveWhitelist
    );

    return {
        clients: Array.isArray(activeClients) ? activeClients : [],
        useCrmWhitelist: useActiveWhitelist,
        cohortAllowedUserIds: Array.from(allowedIds),
        archivedUserIdsForFilter: Array.from(archivedUserIds),
    };
}
